import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import styled, { keyframes } from "styled-components";

const tooltipFadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-50%) translateX(-5px);
  }
  to {
    opacity: 1;
    transform: translateY(-50%) translateX(5px);
  }
`;

const PortalTooltip = styled.div`
  position: fixed;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.main.white};
  background: linear-gradient(
    135deg,
    ${({ theme }) => theme.colors.main.textMain} 0%,
    ${({ theme }) => theme.colors.main.textSecondary} 100%
  );
  border: 1px solid ${({ theme }) => theme.colors.border.secondaryLight};
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  z-index: 2100;
  width: max-content;
  max-width: 200px;
  min-width: 80px;
  opacity: 0;
  visibility: visible;
  transition: opacity 0.2s ease, transform 0.2s ease;
  pointer-events: none;
  transform: translateY(-50%) translateX(-5px);
  
  /* Animate in when mounted */
  animation: ${tooltipFadeIn} 0.2s ease forwards;
  
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: -6px;
    transform: translateY(-50%);
    width: 0;
    height: 0;
    border-top: 6px solid transparent;
    border-bottom: 6px solid transparent;
    border-right: 6px solid ${({ theme }) => theme.colors.main.textMain};
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: -5px;
    transform: translateY(-50%);
    width: 0;
    height: 0;
    border-top: 5px solid transparent;
    border-bottom: 5px solid transparent;
    border-right: 5px solid ${({ theme }) => theme.colors.main.textMain};
  }
`;

export const TooltipPortal = ({ children, text, isVisible, targetRef }) => {
  const [position, setPosition] = useState(null);
  const tooltipRef = useRef(null);

  const updatePosition = () => {
    if (targetRef?.current) {
      const rect = targetRef.current.getBoundingClientRect();
      const newLeft = rect.right + 15;
      const newTop = rect.top + rect.height / 2;
      
      setPosition({ 
        top: newTop, 
        left: newLeft 
      });
    }
  };

  useEffect(() => {
    if (isVisible && targetRef?.current) {
      // Update position immediately when becoming visible
      updatePosition();
      
      // Update position on scroll or resize
      window.addEventListener('scroll', updatePosition);
      window.addEventListener('resize', updatePosition);
      
      return () => {
        window.removeEventListener('scroll', updatePosition);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [isVisible, targetRef]);

  return (
    <>
      {children}
      {isVisible && position && createPortal(
        <PortalTooltip
          ref={tooltipRef}
          isVisible={true}
          style={{
            top: `${position.top}px`,
            left: `${position.left}px`,
          }}
        >
          {text}
        </PortalTooltip>,
        document.body
      )}
    </>
  );
}; 